import { useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createVehicleSchema, type CreateVehicleInput, type Vehicle } from '@wrapforce/shared';
import { Plus, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useVehicleMockupHistory } from '@/features/mockups/api';
import { useCustomer } from './api';
import { useCreateVehicle, useCustomerVehicles, useRevealVin, useUpdateVehicle } from './vehiclesApi';
import { useLocaleFormatters } from '@/lib/format';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';

export function CustomerDetailPage() {
  const { t } = useTranslation('customers');
  const { formatMoney } = useLocaleFormatters();
  const { customerId } = useParams<{ customerId: string }>();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const isManager = membership?.role === 'owner' || membership?.role === 'manager';

  const { data: customer } = useCustomer(workspaceId, customerId);
  const { data: vehicles = [] } = useCustomerVehicles(workspaceId, customerId);
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);

  if (!customer) return null;

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_260px]">
        <Card>
          <CardHeader>
            <CardTitle>{customer.fullName}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <div className="text-muted">{customer.phone || '-'} · {customer.email || '-'}</div>
            {customer.notes ? <p className="mt-2 text-ink">{customer.notes}</p> : null}
            <div className="mt-2 flex gap-4 text-xs text-muted">
              <span>{t('detail.quotesCount', { count: customer.quotesCount })}</span>
              <span>{t('detail.jobsCount', { count: customer.jobsCount })}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.lifetimeValue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-display text-2xl font-bold tabular-nums text-ink">{formatMoney(customer.totalSpentCents)}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.vehiclesTitle', { count: vehicles.length })}</CardTitle>
          <Button size="sm" className="ml-auto gap-1.5" onClick={() => setAddVehicleOpen(true)}>
            <Plus size={14} /> {t('actions.addVehicle')}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {vehicles.map((v) => (
            <VehicleCard key={v.id} vehicle={v} canReveal={isManager} workspaceId={workspaceId} customerId={customerId!} />
          ))}
          {vehicles.length === 0 ? <p className="text-sm text-muted">{t('detail.noVehicles')}</p> : null}
        </CardContent>
      </Card>

      {workspaceId && customerId ? (
        <VehicleDialog open={addVehicleOpen} onOpenChange={setAddVehicleOpen} workspaceId={workspaceId} customerId={customerId} />
      ) : null}
    </div>
  );
}

function VehicleCard({
  vehicle, canReveal, workspaceId, customerId,
}: {
  vehicle: import('@wrapforce/shared').Vehicle; canReveal: boolean; workspaceId: string | null; customerId: string;
}) {
  const { t } = useTranslation('customers');
  const revealVin = useRevealVin(workspaceId);
  const [revealed, setRevealed] = useState<string | null>(null);
  const { data: history = [] } = useVehicleMockupHistory(workspaceId, vehicle.id);
  const [enlargedUrl, setEnlargedUrl] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();

  return (
    <div className="rounded-[var(--radius-input)] border border-border p-3.5">
      <ConfirmDialog />
      <div className="flex items-center gap-3">
        <div className="font-semibold text-ink">
          {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || t('vehicle.unnamed')}
        </div>
        {vehicle.trim ? <span className="text-xs text-muted">{vehicle.trim}</span> : null}
        {vehicle.color ? <span className="text-xs text-muted">{vehicle.color}</span> : null}
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          aria-label={t('actions.editVehicle')}
          className="ml-auto grid h-7 w-7 shrink-0 place-items-center text-muted hover:bg-surface-2 hover:text-ink"
        >
          <Pencil size={14} />
        </button>
      </div>
      <div className="mt-1.5 flex items-center gap-2 font-mono text-xs text-muted">
        <span>{t('vehicle.vinLabel', { vin: revealed ?? vehicle.vinMasked ?? '-' })}</span>
        {canReveal && vehicle.vinMasked && !revealed ? (
          <button
            type="button"
            className="flex items-center gap-1 text-accent-ink hover:underline"
            onClick={async () => {
              const ok = await confirm({
                title: t('vehicle.revealConfirm.title'),
                description: t('vehicle.revealConfirm.description'),
                confirmLabel: t('vehicle.revealConfirm.confirmLabel'),
              });
              if (!ok) return;
              const result = await revealVin.mutateAsync(vehicle.id);
              setRevealed(result.vin);
            }}
          >
            <Eye size={11} /> {t('actions.reveal')}
          </button>
        ) : null}
      </div>
      {vehicle.photos.length > 0 ? (
        <div className="mt-2 flex gap-1.5">
          {vehicle.photos.map((p) => (
            <img key={p.id} src={p.thumbUrl ?? p.url} alt="" className="h-14 w-14 object-cover" />
          ))}
        </div>
      ) : null}

      {history.length > 0 ? (
        <div className="mt-3 border-t border-border pt-2.5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[.1em] text-muted">{t('vehicle.mockupHistory')}</div>
          <div className="flex flex-wrap gap-1.5">
            {history.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => m.url && setEnlargedUrl(m.url)}
                className="h-14 w-14 overflow-hidden border border-border"
                title={`Q-${String(m.quoteNumber).padStart(4, '0')}`}
              >
                {m.url ? <img src={m.thumbUrl ?? m.url} alt="" className="h-full w-full object-cover" /> : null}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="mt-2 text-xs font-semibold text-accent-ink hover:underline"
            onClick={() => navigate(`/quotes/new?customerId=${customerId}&vehicleId=${vehicle.id}`)}
          >
            {t('vehicle.useMockup')}
          </button>
        </div>
      ) : null}

      <Dialog open={!!enlargedUrl} onOpenChange={(open) => !open && setEnlargedUrl(null)} title={t('vehicle.mockupPreview')}>
        {enlargedUrl ? <img src={enlargedUrl} alt="" className="max-h-[75vh] w-full object-contain" /> : null}
      </Dialog>

      {workspaceId ? (
        <VehicleDialog open={editOpen} onOpenChange={setEditOpen} workspaceId={workspaceId} customerId={customerId} vehicle={vehicle} />
      ) : null}
    </div>
  );
}

function VehicleDialog({
  open,
  onOpenChange,
  workspaceId,
  customerId,
  vehicle,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspaceId: string;
  customerId: string;
  // When provided, the dialog edits this vehicle (PATCH) instead of creating.
  vehicle?: Vehicle;
}) {
  const { t } = useTranslation('customers');
  const isEdit = !!vehicle;
  const createVehicle = useCreateVehicle(workspaceId, customerId);
  const updateVehicle = useUpdateVehicle(workspaceId, customerId);
  const yearId = useId();
  const makeId = useId();
  const modelId = useId();
  const trimId = useId();
  const colorId = useId();
  const vinId = useId();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateVehicleInput>({
    resolver: zodResolver(createVehicleSchema),
    // Prefill from the existing vehicle when editing. The raw VIN is never
    // returned (only vinMasked), so the VIN field always starts empty.
    values: vehicle
      ? {
          year: vehicle.year ?? undefined,
          make: vehicle.make || undefined,
          model: vehicle.model || undefined,
          trim: vehicle.trim || undefined,
          color: vehicle.color || undefined,
          vin: '',
        }
      : undefined,
  });

  async function onSubmit(values: CreateVehicleInput) {
    if (vehicle) {
      // A blank VIN field on edit means "keep the VIN on file" rather than
      // clear it - only send vin when the user actually typed a new one.
      const { vin, ...rest } = values;
      await updateVehicle.mutateAsync({ vehicleId: vehicle.id, input: vin ? { ...rest, vin } : rest });
      toast.success(t('toast.vehicleUpdated'));
    } else {
      await createVehicle.mutateAsync(values);
      toast.success(t('toast.vehicleAdded'));
      reset();
    }
    onOpenChange(false);
  }

  const vinPlaceholder = isEdit && vehicle?.vinMasked
    ? t('editVehicleDialog.vinKeepPlaceholder', { vin: vehicle.vinMasked })
    : t('addVehicleDialog.vinPlaceholder');

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={isEdit ? t('editVehicleDialog.title') : t('addVehicleDialog.title')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={yearId}>{t('addVehicleDialog.labels.year')}</Label>
            <Input id={yearId} type="number" {...register('year', { valueAsNumber: true })} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={makeId}>{t('addVehicleDialog.labels.make')}</Label>
            <Input id={makeId} {...register('make')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={modelId}>{t('addVehicleDialog.labels.model')}</Label>
            <Input id={modelId} {...register('model')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={trimId}>{t('addVehicleDialog.labels.trim')}</Label>
            <Input id={trimId} {...register('trim')} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={colorId}>{t('addVehicleDialog.labels.color')}</Label>
          <Input id={colorId} {...register('color')} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={vinId}>{t('addVehicleDialog.labels.vin')}</Label>
          <Input id={vinId} placeholder={vinPlaceholder} {...register('vin')} />
          {isEdit && vehicle?.vinMasked ? <p className="text-xs text-muted">{t('editVehicleDialog.vinKeepHint')}</p> : null}
          <FieldError>{errors.vin?.message}</FieldError>
        </div>
        <Button type="submit" loading={createVehicle.isPending || updateVehicle.isPending} className="mt-1 w-full">
          {isEdit ? t('actions.saveVehicle') : t('actions.addVehicle')}
        </Button>
      </form>
    </Dialog>
  );
}
